'use client';

// npm library imports
import { useCallback } from 'react';

// embla carousel imports
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';

// local component imports
import CarouselArrows from './_CarouselArrows';
import CarouselDots from './_CarouselDots';
import emblaStyles from './embla.module.scss';

// type imports
import { CarouselImage } from '@lib/types';
import type { CSSProperties } from 'react';
import _SingleImageCarousel from './_SingleImageCarousel';

/**
 * Basic Carousel component that displays images in a horizontal slider.
 *
 * @remarks
 * Each image in the carousel need to be the same shape and size otherwise it may not display correctly.
 *
 * @param props - The component props object
 * @param props.images - The array of CarouselImages to display in the carousel
 * @param props.overlap Optional - Used to position the carousel overlapping other sections
 * @returns The CarouselBasic component
 *
 * @example
 * ```tsx
 * <CarouselBasic
 *  images={images}
 *  overlap="top"
 * />
 * ```
 */
const CarouselBasic = ({
    images,
    overlap = 'none',
    controlsColor,
}: {
    images: CarouselImage[];
    overlap?: 'none' | 'top' | 'bottom' | 'singlebottom';
    controlsColor?: string;
}) => {
    // Setup the embla carousel Ref and API with options and autoplay plugin
    const [emblaRef, emblaApi] = useEmblaCarousel(
        { loop: true, skipSnaps: true },
        [Autoplay({ delay: 8000, stopOnInteraction: true })],
    );

    // Function to handle autoplay on navigation button clicks.
    // required for arrows and dots to function.
    const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;

        const resetOrStop =
            autoplay.options.stopOnInteraction === false
                ? autoplay.reset
                : autoplay.stop;

        resetOrStop();
    }, []);

    return (
        <div
            /* Carousel container  with overlap style and dynamic max width */
            className={`${emblaStyles.embla} ${emblaStyles.carouselBasic} ${emblaStyles[overlap]}`}
            style={
                {
                    maxWidth: `${images[0].width}px`,
                    ...(controlsColor !== undefined && {
                        '--controls-color': controlsColor,
                    }),
                } as CSSProperties
            }
        >
            {/* If only one image, use SingleImageCarousel component */}
            {images.length <= 1 ? (
                <_SingleImageCarousel image={images[0]} />
            ) : (
                <>
                    {/* If multiple images, create Embla carousel */}
                    <div className={emblaStyles.embla__viewport} ref={emblaRef}>
                        <div className={emblaStyles.embla__container}>
                            {/* Map through images to create carousel slides */}
                            {images.map(
                                (image: CarouselImage, index: number) => {
                                    return (
                                        <div
                                            className={emblaStyles.embla__slide}
                                            key={index}
                                        >
                                            {/* Carousel Image component */}
                                            <_SingleImageCarousel image={image} />
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>
                    {/* Navigation controls */}
                    <div className={emblaStyles.embla__controls}>
                        <CarouselArrows
                            emblaApi={emblaApi}
                            onNavButtonClick={onNavButtonClick}
                        />
                        <CarouselDots
                            emblaApi={emblaApi}
                            onNavButtonClick={onNavButtonClick}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default CarouselBasic;
